import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { fmtDT, fmtINR, nextAction, senderPublicStatus } from "../../lib/constants";
import StatusPill from "../../components/StatusPill";
import GreenlightPanel from "../../components/GreenlightPanel";
import { CargoAlert } from "../../components/CargoAlert";
import { toast } from "sonner";
import { Info } from "./shipment-detail/_shared";
import { CityOpsPanel, DestinationVehiclePanel, OriginVehiclePanel, RiderAssignPanel } from "./shipment-detail/AssignmentPanels";
import { CommercialGatePanel, PaymentPanel, QuotePanel, SenderDeltaPanel } from "./shipment-detail/CommercialPanels";
import { CancellationPanel } from "./shipment-detail/CancellationPanels";
import { FinanceDocumentsPanel } from "./shipment-detail/FinanceDocumentsPanel";
import { AuditTimeline, DocumentsPanel, LegalDocumentsPanel, NotificationsPanel } from "./shipment-detail/DocsIssuesPanels";
import { PackagesPanel, RiderMeasurementPanel, ScanCompletenessPanel, VerificationReviewPanel } from "./shipment-detail/PackagePanels";
import { OriginCaptainPanel, OutForDeliveryPanel, MarkReceiverReadyPanel, DeliveryOtpPanel, ForceDeliveredPanel, TrackingCredentialsPanel, ReceiverPanel, ShipmentConfirmationPanel, SenderAuthorityLetterPanel, AuthorityLetterCustodyPanel } from "./shipment-detail/WorkflowPanels";
import { SuperAdminPickupPanel } from "./shipment-detail/SuperAdminPickupPanel";
import { ShipmentBlockPanel } from "./shipment-detail/ShipmentBlockPanel";
import { SenderInvoicesPanel } from "./shipment-detail/SenderInvoicesPanel";
import { SupervisorEwbPanel } from "./shipment-detail/SupervisorEwbPanel";
import { SenderContactEdit } from "./shipment-detail/SenderContactEdit";
import { isCompanyInactive } from "../../components/RequireSenderCompany";

export default function ShipmentDetail() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const balanceRef = useRef(null);
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState(null);
  const [users, setUsers] = useState([]);

  const reload = useCallback((attempt = 0) => {
    setLoading(true);
    if (attempt === 0) setLoadErr(null);
    api.get(`/shipments/${id}`)
      .then((r) => { setD(r.data); setLoadErr(null); setLoading(false); })
      .catch((e) => {
        const status = e?.response?.status;
        // B5 — terminal 24h operational-access expiry: no detail exposure; send the user home with
        // a simple non-technical message (their Handled History lives on their home).
        if (e?.code === "TERMINAL_ACCESS_EXPIRED") {
          toast.info("This shipment is closed and is no longer available in your active work.");
          navigate("/app", { replace: true });
          return;
        }
        const transient = !e?.response || [502, 503, 504].includes(status);
        if (transient && attempt < 3) {
          setTimeout(() => reload(attempt + 1), 1200);
        } else {
          setLoadErr(e.displayMessage || "We couldn't load this. Please try again.");
          setLoading(false);
        }
      });
  }, [id, navigate]);
  useEffect(() => { reload(); }, [reload]);
  useEffect(() => {
    if (["SUPER_ADMIN", "CTRL", "CTOP"].includes(profile?.role)) {
      api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
    }
  }, [profile]);

  // AIR12 Captain DELTA — deep-link focus: when arriving via the balance-payment email link
  // (?focus=balance-payment), scroll to and briefly highlight the balance-payment section.
  useEffect(() => {
    if (!d || searchParams.get("focus") !== "balance-payment") return;
    const el = balanceRef.current;
    if (!el) return;
    const t = setTimeout(() => {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.setAttribute("data-focused", "true");
      setTimeout(() => el.removeAttribute("data-focused"), 2400);
    }, 150);
    return () => clearTimeout(t);
  }, [d, searchParams]);

  if (loading && !d) return <div className="p-10 mono text-sm" data-testid="shipment-loading">Loading…</div>;
  if (loadErr && !d) return (
    <div className="p-10 mono text-sm space-y-3" data-testid="shipment-load-error">
      <div className="text-[hsl(var(--danger))]">Could not load this shipment. {loadErr}</div>
      <button className="btn-primary" onClick={() => reload()} data-testid="shipment-retry-button">Retry</button>
    </div>
  );
  if (!d) return <div className="p-10">Not found</div>;
  const s = d.shipment;
  const isAdmin = profile?.role === "SUPER_ADMIN" || profile?.role === "CTOP";
  const isSuperAdmin = profile?.role === "SUPER_ADMIN";
  const canSeeRouting = isAdmin || profile?.role === "CTRL";
  const canSeeCityOps = isSuperAdmin || profile?.role === "CTRL" || profile?.role === "CTOP";
  // Area 2 — destination ownership flags (backend enforces object-level assignment too).
  const isAssignedDestCtop = profile?.role === "CTOP" && s.destination_ctop_id === profile?.id;
  const isAssignedDestCap = profile?.role === "CAP" && s.destination_captain_id === profile?.id;
  const isAssignedDestCtrl = profile?.role === "CTRL" && s.destination_city_controller_id === profile?.id;
  // A9 (Founder-locked) — assigned-side CTRL may finalise its side's vehicles (+ Dest: Receiver Ready, Out for Delivery).
  const isAssignedSrcCtrl = profile?.role === "CTRL" && s.source_city_controller_id === profile?.id;
  // Area 3 — side-scoped operational flags (backend enforces the same rules).
  const isSuperAdminOnly = profile?.role === "SUPER_ADMIN";
  const isSourceCtop = profile?.role === "CTOP" && s.source_ctop_id === profile?.id;
  const isDestCtop = isAssignedDestCtop;
  const isSourceCap = profile?.role === "CAP" && s.source_captain_id === profile?.id;
  const isSender = profile?.role === "SENDER";
  const canActSource = isSuperAdminOnly || isSourceCtop;
  // A13 — Captains / Riders never see Air12 commercial charges or payment amounts.
  const isCapRid = ["CAP", "RID"].includes(profile?.role);
  // A11 — INACTIVE company: no pre-MAIN commercial actions / new bookings from this page.
  const companyInactive = isCompanyInactive(profile);
  // §6 — commercial/DELTA visibility: Sender, Source CTOP, Source Controller, Super Admin.
  // M3 — any airport-related CTRL may READ commercial/DELTA (the detail page is backend-gated to
  // origin/destination-airport CTRLs; read-only — no action buttons are exposed to CTRL).
  const isCtrl = profile?.role === "CTRL";
  const canSeeCommercial = isSuperAdmin || isSourceCtop || isCtrl;
  // C8 — Destination-side CTRL has no DELTA action; the empty DELTA panel is hidden for it.
  const isDestSideCtrl = isCtrl && !isAssignedSrcCtrl && profile?.airport_code !== s.source_airport;
  const canSeeDeltaPanel = canSeeCommercial && !isDestSideCtrl;
  const canActDest = isSuperAdminOnly || isDestCtop;
  const canDispatch = isSuperAdmin || isAssignedDestCtop || isAssignedDestCap || isAssignedDestCtrl;

  return (
    <div className="max-w-[1600px] mx-auto p-6">
      <div className="mb-4 text-xs"><Link to="/app" className="underline" data-testid="back-link">← All shipments</Link></div>

      <div className="grid-card grid-card-strong p-6 mb-4">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="overline">SHIPMENT NUMBER</div>
            <div className="id-chip id-chip-strong text-base mt-1" data-testid="shipment-number">{s.public_shipment_number}</div>
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <span className="id-chip">{s.route_code}</span>
              <span className="text-xs text-[hsl(var(--muted))]">{s.source_airport} → {s.destination_airport}</span>
              {isSender ? (
                <span className="status-pill status-info" data-testid="sender-public-status">
                  {senderPublicStatus(s)}
                </span>
              ) : (
                <StatusPill status={s.current_status} data-testid="current-status" />
              )}
            </div>
          </div>
          <div className="text-right">
            <div className="overline">CHARGEABLE WEIGHT</div>
            <div className="heading text-2xl mono">{s.total_chargeable_weight_kg} kg</div>
            {!isCapRid && (
              <>
                <div className="overline mt-3">TOTAL PAYABLE</div>
                <div className="heading text-2xl mono">{fmtINR(s.total_payable)}</div>
              </>
            )}
          </div>
        </div>
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <Info label="GOODS">{s.goods_description}</Info>
          <Info label="COMMODITY">{s.commodity_category}</Info>
          <Info label="INVOICE VALUE">{fmtINR(s.declared_invoice_value)}</Info>
          <Info label="CREATED">{fmtDT(s.created_at)}</Info>
          <Info label="SENDER CONTACT">
            <span data-testid="sender-contact-mobile">{s.sender_contact_mobile ? `+91 ${s.sender_contact_mobile}` : "—"}</span>
            {isSender && !companyInactive && s.sender_contact_correction_allowed && (
              <SenderContactEdit shipmentId={s.id} current={s.sender_contact_mobile} onSaved={reload} />
            )}
          </Info>
          <Info label="RECEIVER CONTACT"><span data-testid="receiver-contact-mobile">{d.receiver?.mobile ? `+91 ${d.receiver.mobile}` : "—"}</span></Info>
        </div>
        {!isSender && (
          <div className="mt-4 px-3 py-2 rounded bg-[hsl(var(--accent)/0.08)] border border-[hsl(var(--accent)/0.3)] text-sm" data-testid="next-action-banner">
            <span className="overline text-[10px] mr-2">NEXT ACTION</span>
            <span className="font-semibold">{d.operator_next_action || nextAction(s.current_status)}</span>
          </div>
        )}
        {isSender && !companyInactive && d.revision?.allowed && (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 px-3 py-3 rounded border border-[hsl(var(--border-strong))]" data-testid="edit-booking-cta">
            <span className="text-sm text-[hsl(var(--muted))]">Need to change your booking details before payment?</span>
            <Link to={`/sender/shipments/${s.id}/revise`} className="btn-ghost text-sm" data-testid="edit-booking-button">Edit booking</Link>
          </div>
        )}
        {profile?.role === "SENDER" && !companyInactive && s.current_status === "CANCELLED" && (
          <div className="mt-4 px-3 py-3 rounded bg-[hsl(var(--card))] border border-[hsl(var(--border-strong))] flex flex-wrap items-center justify-between gap-3" data-testid="book-another-cta">
            <span className="text-sm text-[hsl(var(--muted))]">This shipment was cancelled. Need to ship again?</span>
            <Link to="/sender/new" className="btn-primary text-sm" data-testid="book-another-shipment-btn">Book another shipment</Link>
          </div>
        )}
      </div>

      {!isSender && (
        <CargoAlert
          restrictedGoods={s.restricted_goods_declared}
          fragilePackages={(d.packages || []).filter((p) => p.fragile).map((p) => p.public_label)}
          className="mb-4"
          testid="shipment-cargo-alert"
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* LEFT 2/3 */}
        <div className="lg:col-span-2 space-y-4">
          {canSeeCityOps && <CityOpsPanel d={d} reload={reload} users={users} profile={profile} />}
          {(canSeeRouting || canSeeCityOps) && <GreenlightPanel shipmentId={d.shipment.id} />}
          {["SUPER_ADMIN", "CTOP", "CTRL", "CAP", "RID"].includes(profile?.role) && (
            <ShipmentBlockPanel d={d} profile={profile} />
          )}
          <CommercialGatePanel d={d} reload={reload} profile={profile} isAdmin={canActSource} />
          <CancellationPanel d={d} reload={reload} profile={profile} />
          {(isSender || isSuperAdmin) && <FinanceDocumentsPanel d={d} profile={profile} />}
          <div id="balance-payment" ref={balanceRef} className="scroll-mt-24 rounded transition-shadow data-[focused=true]:ring-2 data-[focused=true]:ring-[hsl(var(--accent))]" data-testid="balance-payment-section">
            <SenderDeltaPanel d={d} reload={reload} profile={profile} />
          </div>
          <PackagesPanel d={d} reload={reload} isAdmin={isAdmin} isSender={isSender} />
          {canSeeDeltaPanel && <VerificationReviewPanel d={d} reload={reload} profile={profile} users={users} />}
          {["SUPER_ADMIN", "CTOP", "CTRL", "CAP"].includes(profile?.role) && (
            <RiderMeasurementPanel
              d={d}
              reload={reload}
              isSuperAdmin={isSuperAdmin}
              isSourceCtop={isSourceCtop}
              correctionWindowOpen={!!d.correction_window_open}
            />
          )}
          <DocumentsPanel d={d} reload={reload} isAdmin={isAdmin} isSuperAdmin={isSuperAdminOnly} isSourceCtop={isSourceCtop} isSourceCap={isSourceCap} isSender={isSender} senderReady={!!s.sender_ready_at} />
          {isSender && <SenderInvoicesPanel d={d} reload={reload} profile={profile} />}
          {["SUPER_ADMIN", "CTRL", "CTOP"].includes(profile?.role) && <SupervisorEwbPanel d={d} profile={profile} />}
          {!isCapRid && <QuotePanel d={d} reload={reload} isSuperAdmin={isSuperAdmin} />}
          {(isSender || isSuperAdmin) && <LegalDocumentsPanel shipmentId={s.id} />}
          {!isCapRid && <PaymentPanel d={d} reload={reload} isAdmin={canActSource} />}
          {(isSender || isSuperAdmin || isSourceCtop || isCtrl) && <ShipmentConfirmationPanel d={d} reload={reload} />}
          {(isSender || isSuperAdmin || ["CTRL", "CTOP", "CAP", "RID"].includes(profile && profile.role)) && <SenderAuthorityLetterPanel d={d} reload={reload} />}
          {(isSuperAdmin || ["CTRL", "CTOP", "CAP"].includes(profile && profile.role)) && <AuthorityLetterCustodyPanel d={d} reload={reload} />}
          {canActSource && <RiderAssignPanel d={d} reload={reload} users={users} />}
          {isSuperAdminOnly && <SuperAdminPickupPanel d={d} reload={reload} />}
          {(canActSource || isAssignedSrcCtrl) && <OriginVehiclePanel d={d} reload={reload} />}
          {!isSender && <OriginCaptainPanel d={d} reload={reload} canReceipt={isSourceCap || isSourceCtop || isSuperAdminOnly} canAwb={isSourceCap || isSuperAdminOnly} canHandover={isSourceCap} />}
          {(isSuperAdminOnly || isDestCtop || isAssignedDestCtrl) && d.shipment.current_status === "AWAITING_RECEIVER_READY" && (
            <MarkReceiverReadyPanel d={d} reload={reload} />
          )}
          {(isSuperAdminOnly || isDestCtop || isAssignedDestCtrl) && ["RECEIVER_READY_CONFIRMED", "DESTINATION_VEHICLE_ASSIGNED"].includes(d.shipment.current_status) && (
            <DestinationVehiclePanel d={d} reload={reload} />
          )}
          {canDispatch && d.shipment.current_status === "DESTINATION_VEHICLE_ASSIGNED" && (
            <OutForDeliveryPanel d={d} reload={reload} />
          )}
          {(isSuperAdminOnly || isDestCtop || isAssignedDestCtrl) &&
            ["RECEIVER_READY_CONFIRMED", "DESTINATION_VEHICLE_ASSIGNED", "OUT_FOR_DELIVERY"].includes(d.shipment.current_status) && (
            <DeliveryOtpPanel d={d} reload={reload} />
          )}
          {(isSuperAdminOnly || isDestCtop || isAssignedDestCtrl || isAssignedDestCap) &&
            d.shipment.current_status === "OUT_FOR_DELIVERY" && (
            <ForceDeliveredPanel d={d} reload={reload} canExecute={isSuperAdminOnly} />
          )}
          {isSuperAdminOnly && <TrackingCredentialsPanel d={d} reload={reload} />}
        </div>
        {/* RIGHT 1/3 */}
        <div className="space-y-4">
          <ReceiverPanel d={d} />
          {!isSender && <ScanCompletenessPanel d={d} />}
          {!isSender && <AuditTimeline d={d} />}
          {(isSuperAdmin || ["CTRL", "CTOP"].includes(profile?.role)) && <NotificationsPanel d={d} canResend={isSuperAdmin} />}
        </div>
      </div>
    </div>
  );
}
